import { useCallback, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { useGetAllBusinessPartnersWithOrderStats } from '@/api/business-partner/business-partner.hooks';
import type { BusinessPartnerWithStats } from '@/api/business-partner/business-partner.types';
import { useGridState } from '@hooks/use-grid-state';
import { usePermissions } from '@/hooks/use-permissions';
import {
  DsSearchInput,
  DsSegmented,
  type DsSegmentedOption,
} from '@/modules/internal-v2/ds';
import { SalesPersonPicker } from '@/modules/internal-v2/shared/sales-person-picker.component';
import { DsTable } from '../../shared/ds-table.component';
import { BpTypeBadge } from './bp-type-badge.component';

type BpFilter = 'all' | 'customer' | 'supplier';

/** Context provided by {@link BpBrowser} to its detail-panel routes. */
export interface BpListContext {
  partners: BusinessPartnerWithStats[];
  partnersPending: boolean;
}

const FILTER_OPTIONS: DsSegmentedOption<BpFilter>[] = [
  { value: 'all', label: 'Alle' },
  { value: 'customer', label: 'Kunden' },
  { value: 'supplier', label: 'Lieferanten' },
];

const fmtCompact = (v: number) => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
};

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
};

function matchesBp(bp: BusinessPartnerWithStats, q: string): boolean {
  return (
    String(bp.id).includes(q) ||
    bp.name.toLowerCase().includes(q) ||
    bp.legalName.toLowerCase().includes(q) ||
    (bp.email?.toLowerCase().includes(q) ?? false) ||
    (bp.defaultContactPersonName?.toLowerCase().includes(q) ?? false) ||
    (bp.vatId?.toLowerCase().includes(q) ?? false)
  );
}

const col = createColumnHelper<BusinessPartnerWithStats>();

const masterColumns = [
  col.accessor('id', {
    header: 'ID',
    size: 60,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 'var(--fs-xs)' }}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('legalName', {
    header: 'Name',
    meta: { flex: true },
    cell: (info) => (
      <span style={{ fontWeight: 500, color: 'var(--ink)' }}>
        {info.getValue() || info.row.original.name}
      </span>
    ),
    sortingFn: 'text',
  }),
  col.accessor('type', {
    header: 'Typ',
    size: 50,
    cell: (info) => <BpTypeBadge type={info.getValue()} />,
    enableSorting: false,
  }),
  col.accessor('revenue', {
    header: 'Umsatz',
    size: 80,
    meta: { numeric: true },
    cell: (info) => {
      const v = info.getValue();
      return (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 500,
            color: v > 0 ? 'var(--ink)' : 'var(--ink-4)',
          }}
        >
          {v > 0 ? `€${fmtCompact(v)}` : '—'}
        </span>
      );
    },
  }),
  col.accessor('totalOrders', {
    header: 'Auftr.',
    size: 50,
    meta: { numeric: true },
    cell: (info) => (
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          color: info.getValue() > 0 ? 'var(--ink-2)' : 'var(--ink-4)',
        }}
      >
        {info.getValue() || '—'}
      </span>
    ),
  }),
  col.accessor('lastOrderDate', {
    header: 'Letzte',
    size: 75,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--ink-2)',
          }}
        >
          {v ? fmtDate(v) : '—'}
        </span>
      );
    },
    sortingFn: (a, b) => {
      const da = a.original.lastOrderDate ? new Date(a.original.lastOrderDate).getTime() : 0;
      const db = b.original.lastOrderDate ? new Date(b.original.lastOrderDate).getTime() : 0;
      return da - db;
    },
  }),
];

/**
 * "Geschäftspartner" tab content — the search-driven master list plus the
 * resizable detail pane. Rendered as a layout route so its `<Outlet>` hosts
 * the `:bpId` partner detail; provides {@link BpListContext} to those routes.
 */
export function BpBrowser() {
  const { getCustom, setCustom } = useGridState('business-partners-v2');
  const filter = getCustom<BpFilter>('filter', 'customer');
  const setFilter = useCallback((v: BpFilter) => setCustom('filter', v), [setCustom]);

  const [query, setQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'lastOrderDate', desc: true },
  ]);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { bpId } = useParams<{ bpId: string }>();
  const selectedId = bpId ? Number(bpId) : null;

  // Sales-person filter is only meaningful when the user can actually see
  // partners outside their own scope; for `OWN`-scope users the backend has
  // already narrowed the list to their own partners.
  const { businessPartnerScope, salesPersonCode: ownSalesPersonCode } =
    usePermissions();
  const canFilterBySalesPerson = businessPartnerScope === 'ALL';

  // Persist the sales-person filter across navigation, defaulting to the
  // user's own code on first visit so they land on their own partners. The
  // value can be cleared (null) to fall back to "all sales persons".
  const salesPersonFilterRaw = getCustom<number | null | undefined>(
    'salesPersonFilter',
    undefined,
  );
  const salesPersonFilter =
    salesPersonFilterRaw === undefined ? ownSalesPersonCode : salesPersonFilterRaw;
  const setSalesPersonFilter = useCallback(
    (v: number | null) => setCustom('salesPersonFilter', v),
    [setCustom],
  );

  const { data: allPartners = [], isPending } = useGetAllBusinessPartnersWithOrderStats();

  const filtered = useMemo(() => {
    let list = allPartners;
    if (filter === 'customer') list = list.filter((bp) => bp.type === 'cCustomer');
    else if (filter === 'supplier') list = list.filter((bp) => bp.type === 'cSupplier');
    if (canFilterBySalesPerson && salesPersonFilter != null) {
      list = list.filter((bp) => bp.defaultSalesPersonId === salesPersonFilter);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((bp) => matchesBp(bp, q));
    }
    return list;
  }, [allPartners, filter, query, canFilterBySalesPerson, salesPersonFilter]);

  const table = useReactTable({
    data: filtered,
    columns: masterColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.id),
    enableSortingRemoval: false,
  });

  // Selecting a partner navigates into its detail route. Keep the active
  // detail tab (Aufträge/Kontakte) when switching between partners, but drop
  // any drilled-in order/contact id.
  const handleRowClick = useCallback(
    (bp: BusinessPartnerWithStats) => {
      const tabSeg = pathname.includes('/kontakte')
        ? '/kontakte'
        : pathname.includes('/auftraege')
          ? '/auftraege'
          : '';
      navigate(`/i/business-partners/${bp.id}${tabSeg}`);
    },
    [navigate, pathname],
  );

  return (
    <div className="bp-browser">
      <div className="bp-command-bar">
        <DsSearchInput
          className="bp-cmd-search"
          value={query}
          onSearch={setQuery}
          debounce={0}
          placeholder="ID, Name, E-Mail, USt-ID…"
          autoFocus
        />
        <DsSegmented
          options={FILTER_OPTIONS}
          value={filter}
          onChange={setFilter}
          ariaLabel="Partner-Typ-Filter"
        />
        {canFilterBySalesPerson && (
          <div className="bp-cmd-salesperson">
            <SalesPersonPicker
              value={salesPersonFilter ?? null}
              onChange={setSalesPersonFilter}
              placeholder="Verkäufer (alle)"
              emptyLabel="Alle Verkäufer"
            />
          </div>
        )}
        <span className="bp-cmd-count">
          <b>{filtered.length}</b> Partner
        </span>
      </div>

      <Group
        orientation="horizontal"
        className="bp-split"
        id="bp-split"
        defaultLayout={{ 'bp-master': 42, 'bp-detail': 58 }}
      >
        <Panel id="bp-master" minSize={25} className="bp-master-panel">
          {isPending ? (
            <div className="bp-empty">
              <div>
                <div className="big">Laden…</div>
              </div>
            </div>
          ) : (
            <DsTable
              table={table}
              fixedLayout
              selectedRowId={selectedId != null ? String(selectedId) : undefined}
              onRowClick={handleRowClick}
              rowClassName={(row) => (row.openOrders > 0 ? 'bp-row-has-open' : '')}
              emptyMessage="Keine Partner gefunden."
            />
          )}
        </Panel>
        <Separator className="bp-resize-handle" />
        <Panel id="bp-detail" minSize={30} className="bp-detail-wrap">
          <Outlet
            context={
              { partners: allPartners, partnersPending: isPending } satisfies BpListContext
            }
          />
        </Panel>
      </Group>
    </div>
  );
}
